import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SmsAbstractAppPage } from '../sms-abstract-app-page';
import { CompetitionService } from '../../providers/competition.service';
import { Competition } from '../../models/competition';
import { Section } from '../../models/section';
import { I_competition_entry } from '../../models/i_competition_entry.interface';
import { Competition_entry } from '../../models/competition_entry';
import { Section_entry } from '../../models/section_entry';
import { Entry_type } from '../../models/entry_type';
import { Person } from '../../models/person';
import { faPlus, faBinoculars, faTrash, faCheck } from '@fortawesome/fontawesome-pro-regular';
import { ModalChooseSectionComponent } from '../modal-choose-section/modal-choose-section.component';
import { ModalChooseCompetitionComponent } from '../modal-choose-competition/modal-choose-competition.component';
import { debounceTime, distinctUntilChanged, Observable, switchMap, tap, catchError, of } from 'rxjs';

@Component({
  standalone: false,
  selector: 'edit-competition-entries',
  templateUrl: './edit-competition-entries.component.html',
  styleUrls: ['./edit-competition-entries.component.css']
})
export class EditCompetitionEntriesComponent extends SmsAbstractAppPage implements OnInit, OnDestroy {

  @Input() entry_id: string = '';
  @Input() riders: Array<Person>;
  @Input() entry_type: Entry_type;
  @Input() pending_competition_entry_list: I_competition_entry[] = [];
  @Input() auto_added_ids: Set<string> = new Set();

  @Output() competition_entered$: EventEmitter<Competition_entry> = new EventEmitter<Competition_entry>();
  @Output() competition_removed$: EventEmitter<string> = new EventEmitter<string>();

  comp_choice_model: Competition = new Competition({});
  searching_comps = false;
  searching_comps_failed = false;

  section_choice_model: Section = new Section({});
  searching_sections = false;
  searching_sections_failed = false;

  selected_rider: Person = new Person({});

  last_added: { name: string; type: 'section' | 'competition' } | null = null;
  private last_added_timeout: any;

  faPlus = faPlus;
  faBinoculars = faBinoculars;
  faTrash = faTrash;
  faCheck = faCheck;

  constructor(private competitionService: CompetitionService, private modal: NgbModal) {
    super();
  }

  ngOnInit() {
    if (this.riders.length > 0) {
      this.selected_rider = this.riders[0];
    }
  }

  ngOnDestroy() {
    clearTimeout(this.last_added_timeout);
  }

  isAutoAdded(entry: I_competition_entry): boolean {
    return entry.is_section_entry && this.auto_added_ids.has(entry.section_id);
  }

  private showAddFeedback(name: string, type: 'section' | 'competition') {
    clearTimeout(this.last_added_timeout);
    this.last_added = { name, type };
    this.last_added_timeout = setTimeout(() => { this.last_added = null; }, 3000);
  }

  searchCompByName = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching_comps = true)),
      switchMap(term =>
        this.competitionService.getObservableCompetitionListByNameAtHorseShowForEntryType(
          this.entry_type.horse_show_id, term, this.entry_type.number
        ).pipe(
          tap(() => (this.searching_comps_failed = false)),
          catchError(() => {
            this.searching_comps_failed = true;
            return of([]);
          }),
        ),
      ),
      tap(() => (this.searching_comps = false))
    );

  comp_formatter = (c: { name: string }) => c.name;

  searchSectionByName = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching_sections = true)),
      switchMap(term =>
        this.competitionService.getObservableSectionListByNameAtHorseShowForEntryType(
          this.entry_type.horse_show_id, term, this.entry_type.number
        ).pipe(
          tap(() => (this.searching_sections_failed = false)),
          catchError(() => {
            this.searching_sections_failed = true;
            return of([]);
          }),
        ),
      ),
      tap(() => (this.searching_sections = false))
    );

  section_formatter = (c: { name: string }) => c.name;

  handleEnterCompetition(rider: Person, competition_number: number) {
    this.competitionService.getCompetitionByNumberAtHorseShow(
      this.entry_type.horse_show_id, competition_number, this.entry_type.number
    ).then(competition => {
      const competition_entry = this.getCompetitionEntryInProgress(rider, competition);
      this.competition_entered$.emit(competition_entry);
      this.showAddFeedback(competition.name, 'competition');
      this.comp_choice_model = new Competition({});
    }).catch(error => { this.handleError(error); });
  }

  handleEnterSection(rider: Person, section_number: number) {
    this.competitionService.getSectionByNumberAtHorseShow(
      this.entry_type.horse_show_id, section_number, this.entry_type.number
    ).then(section => {
      const section_entry = this.getSectionEntryInProgress(rider, section);
      this.competition_entered$.emit(section_entry);
      this.showAddFeedback(section.name, 'section');
      this.section_choice_model = new Section({});
    }).catch(error => { this.handleError(error); });
  }

  handleEnterSectionDirect(event: { item: Section; preventDefault: () => void }, rider: Person) {
    event.preventDefault();
    const section_entry = this.getSectionEntryInProgress(rider, event.item);
    this.competition_entered$.emit(section_entry);
    this.showAddFeedback(event.item.name, 'section');
    this.section_choice_model = new Section({});
  }

  handleEnterCompetitionDirect(event: { item: Competition; preventDefault: () => void }, rider: Person) {
    event.preventDefault();
    const competition_entry = this.getCompetitionEntryInProgress(rider, event.item);
    this.competition_entered$.emit(competition_entry);
    this.showAddFeedback(event.item.name, 'competition');
    this.comp_choice_model = new Competition({});
  }

  handleDisplaySectionList() {
    if (this.riders.length > 0 && this.selected_rider.id == undefined) {
      this.selected_rider = this.riders[0];
    }

    this.competitionService.getSectionListByHorseShowAndEntryType(
      this.entry_type.horse_show_id, this.entry_type.number
    ).then(section_list => {
      const modal_ref = this.modal.open(ModalChooseSectionComponent, { size: 'lg' });
      modal_ref.componentInstance.section_list = section_list;
      modal_ref.componentInstance.riders = this.riders;
      modal_ref.componentInstance.selected_rider = this.selected_rider;
      modal_ref.componentInstance.pending_competition_entry_list = this.pending_competition_entry_list;

      modal_ref.componentInstance.section_selected$.subscribe((selected_section: Section) => {
        const active_rider: Person = modal_ref.componentInstance.selected_rider;
        this.handleEnterSection(active_rider, selected_section.number);
      }, error => { this.handleError(error); });

      modal_ref.componentInstance.section_removed$.subscribe((selected_section: Section) => {
        this.competition_removed$.emit(selected_section.id);
      }, error => { this.handleError(error); });

    }).catch(error => { this.handleError(error); });
  }

  handleDisplayCompetitionList() {
    if (this.riders.length > 0 && this.selected_rider.id == undefined) {
      this.selected_rider = this.riders[0];
    }

    this.competitionService.getCompetitionListByHorseShowAndEntryType(
      this.entry_type.horse_show_id, this.entry_type.number
    ).then(competition_list => {
      const modal_ref = this.modal.open(ModalChooseCompetitionComponent, { size: 'lg' });
      modal_ref.componentInstance.competition_list = competition_list;
      modal_ref.componentInstance.riders = this.riders;
      modal_ref.componentInstance.selected_rider = this.selected_rider;
      modal_ref.componentInstance.pending_competition_entry_list = this.pending_competition_entry_list;

      modal_ref.componentInstance.competition_selected$.subscribe((selected_competition: Competition) => {
        const active_rider: Person = modal_ref.componentInstance.selected_rider;
        this.handleEnterCompetition(active_rider, selected_competition.number);
      }, error => { this.handleError(error); });

      modal_ref.componentInstance.competition_removed$.subscribe((selected_competition: Competition) => {
        this.competition_removed$.emit(selected_competition.id);
      }, error => { this.handleError(error); });

    }).catch(error => { this.handleError(error); });
  }

  private getBaseEntryInProgress(rider: Person, is_section: boolean): I_competition_entry {
    const base: any = {
      horse_show_id: this.entry_type.horse_show_id,
      entry_id: this.entry_id,
      rider_person_id: rider.id,
      person_name_rider: (rider.first_name + ' ' + rider.last_name),
      rider_usef_id: rider.usef_number,
      rider_ec_id: rider.ec_number,
      rider_fei_id: rider.fei_id
    };
    return is_section ? new Section_entry(base) : new Competition_entry(base);
  }

  private getCompetitionEntryInProgress(rider: Person, competition: Competition): Competition_entry {
    const entry = this.getBaseEntryInProgress(rider, false);
    entry.competition_id = competition.id;
    entry.competition_number = competition.number;
    entry.competition_name = competition.name;
    entry.section_id = competition.section_id;
    entry.section_number = competition.section_number;
    return entry;
  }

  private getSectionEntryInProgress(rider: Person, section: Section): Section_entry {
    const entry = this.getBaseEntryInProgress(rider, true);
    entry.section_number = section.number;
    entry.section_id = section.id;
    entry.section_name = section.name;
    return entry;
  }
}
